import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Sun, Sunset, FileText, MessageCircleHeart } from "lucide-react";

const SUB_DEFS = [
  {
    key: "morning_prov",
    label: "Morgon-prov-push",
    desc: "Pushar 'Lycka till!' till barnet om hen har prov idag.",
    Icon: Sun,
    defaultTime: "07:30",
  },
  {
    key: "afternoon_focus",
    label: "Eftermiddagens fokus",
    desc: "Sammanfattar imorgondagens läxor + extra-ämnen att jobba med.",
    Icon: Sunset,
    defaultTime: "16:00",
  },
  {
    key: "countdown",
    label: "Prov-countdown",
    desc: "Pushar 3/2/1 dagar innan ett prov med snabb-quiz + skattning.",
    Icon: FileText,
    defaultTime: "16:00",
  },
  {
    key: "child_pokes",
    label: "Edit check-in (inaktivitet)",
    desc: "Pushar en personlig 'hur mår du?'-bubbla från Edit när barnet inte varit i appen på över ett dygn. Vuxna får en kopia.",
    Icon: MessageCircleHeart,
    defaultTime: null,  // ingen tid — körs som cron 14:00–19:00
  },
];

export function StudyRemindersSection({ users = [] }) {
  // users är alla barn (filtrerat utifrån)
  const children = users.filter((u) => u.is_child);
  const [selectedChild, setSelectedChild] = useState(children[0]?.id || null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (children.length && !selectedChild) setSelectedChild(children[0].id);
  }, [children, selectedChild]);

  useEffect(() => {
    if (!selectedChild) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/study-reminders/prefs/${selectedChild}`);
        if (!cancel) setPrefs(data);
      } catch (e) {
        if (!cancel) toast.error("Kunde inte ladda inställningar");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [selectedChild]);

  const update = (key, patch) => {
    setPrefs((p) => ({ ...p, [key]: { ...p[key], ...patch } }));
  };

  const addExtra = () => {
    setPrefs((p) => ({
      ...p,
      afternoon_focus: {
        ...p.afternoon_focus,
        extras: [...(p.afternoon_focus.extras || []), { label: "Läsning", minutes: 20 }],
      },
    }));
  };

  const removeExtra = (idx) => {
    setPrefs((p) => ({
      ...p,
      afternoon_focus: {
        ...p.afternoon_focus,
        extras: p.afternoon_focus.extras.filter((_, i) => i !== idx),
      },
    }));
  };

  const updateExtra = (idx, patch) => {
    setPrefs((p) => ({
      ...p,
      afternoon_focus: {
        ...p.afternoon_focus,
        extras: p.afternoon_focus.extras.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
      },
    }));
  };

  const toggleDay = (n) => {
    setPrefs((p) => {
      const set = new Set(p.countdown.days_before || []);
      if (set.has(n)) set.delete(n); else set.add(n);
      return {
        ...p,
        countdown: { ...p.countdown, days_before: [...set].sort((a, b) => b - a) },
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        morning_prov: {
          enabled: prefs.morning_prov.enabled,
          time: prefs.morning_prov.time,
        },
        afternoon_focus: {
          enabled: prefs.afternoon_focus.enabled,
          time: prefs.afternoon_focus.time,
          extras: (prefs.afternoon_focus.extras || []).filter((e) => e.label?.trim()),
        },
        countdown: {
          enabled: prefs.countdown.enabled,
          time: prefs.countdown.time,
          days_before: prefs.countdown.days_before?.length
            ? prefs.countdown.days_before
            : [3, 2, 1],
        },
        child_pokes: {
          enabled: prefs.child_pokes?.enabled ?? true,
        },
      };
      await api.put(`/study-reminders/prefs/${selectedChild}`, payload);
      toast.success("Inställningar sparade");
    } catch (e) {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  if (!children.length) {
    return (
      <p className="text-sm text-[color:var(--text-secondary)]">
        Lägg till barn under "Familj" först för att konfigurera studie-påminnelser.
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="study-reminders-section">
      {/* Child picker */}
      <div className="flex gap-2 flex-wrap">
        {children.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedChild(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
              selectedChild === c.id
                ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border)]"
            }`}
            data-testid={`study-child-${c.id}`}
          >
            {c.name?.split(" ")[0]}
          </button>
        ))}
      </div>

      {loading || !prefs ? (
        <div className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)]">
          <Loader2 size={14} className="animate-spin" /> Laddar…
        </div>
      ) : (
        <>
          {SUB_DEFS.map(({ key, label, desc, Icon, defaultTime }) => (
            <div key={key} className="card-soft p-4" data-testid={`study-card-${key}`}>
              <div className="flex items-start gap-3">
                <Icon size={18} className="text-[color:var(--moss)] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium">{label}</p>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={prefs[key].enabled}
                        onChange={(e) => update(key, { enabled: e.target.checked })}
                        className="rounded"
                        data-testid={`study-${key}-toggle`}
                      />
                      <span>Aktivt</span>
                    </label>
                  </div>
                  <p className="text-xs text-[color:var(--text-tertiary)] mt-0.5 leading-snug">
                    {desc}
                  </p>
                  {prefs[key].enabled && defaultTime && (
                    <div className="mt-3 flex items-center gap-2 text-xs">
                      <span className="text-[color:var(--text-secondary)]">Tid:</span>
                      <input
                        type="time"
                        value={prefs[key].time || defaultTime}
                        onChange={(e) => update(key, { time: e.target.value })}
                        className="border border-[color:var(--border)] rounded-lg px-2 py-1 text-xs"
                        data-testid={`study-${key}-time`}
                      />
                      <span className="text-[color:var(--text-tertiary)]">vardagar</span>
                    </div>
                  )}

                  {/* afternoon_focus — extras */}
                  {key === "afternoon_focus" && prefs[key].enabled && (
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
                        Extra ämnen att fokusera på dagligen
                      </p>
                      <div className="space-y-1.5">
                        {(prefs.afternoon_focus.extras || []).map((e, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={e.label}
                              onChange={(ev) => updateExtra(i, { label: ev.target.value })}
                              placeholder="Läsning, matte, glosor…"
                              className="flex-1 border border-[color:var(--border)] rounded-lg px-2 py-1 text-xs"
                              data-testid={`study-extra-label-${i}`}
                            />
                            <input
                              type="number"
                              min="5"
                              max="120"
                              value={e.minutes}
                              onChange={(ev) => updateExtra(i, { minutes: parseInt(ev.target.value || 20, 10) })}
                              className="w-14 border border-[color:var(--border)] rounded-lg px-2 py-1 text-xs"
                              data-testid={`study-extra-minutes-${i}`}
                            />
                            <span className="text-[10px] text-[color:var(--text-tertiary)]">min</span>
                            <button
                              onClick={() => removeExtra(i)}
                              className="p-1 text-[color:var(--text-tertiary)] hover:text-red-500"
                              aria-label="Ta bort"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={addExtra}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--moss)] hover:underline"
                        data-testid="study-extra-add"
                      >
                        <Plus size={11} /> Lägg till ämne
                      </button>
                    </div>
                  )}

                  {/* countdown — days */}
                  {key === "countdown" && prefs[key].enabled && (
                    <div className="mt-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-1.5">
                        Skicka push så här många dagar innan provet
                      </p>
                      <div className="flex gap-1.5 flex-wrap">
                        {[5, 4, 3, 2, 1, 0].map((n) => {
                          const active = (prefs.countdown.days_before || []).includes(n);
                          return (
                            <button
                              key={n}
                              onClick={() => toggleDay(n)}
                              className={`text-xs px-2.5 py-1 rounded-full border ${
                                active
                                  ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
                                  : "bg-white text-[color:var(--text-secondary)] border-[color:var(--border)]"
                              }`}
                              data-testid={`study-day-${n}`}
                            >
                              {n === 0 ? "På provdagen" : `${n} dagar innan`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button
            onClick={save}
            disabled={saving}
            className="w-full bg-[color:var(--moss)] text-white text-sm font-medium px-4 py-2.5 rounded-full hover:opacity-90 disabled:opacity-50"
            data-testid="study-save"
          >
            {saving ? "Sparar…" : "Spara inställningar"}
          </button>

          <p className="text-[11px] text-[color:var(--text-tertiary)] leading-snug">
            Påminnelserna går till barnet primärt. Föräldrarna får en kopia. Mail
            skickas också till barnets extra-mejl (iCloud) som du angett under
            "Familj".
          </p>
        </>
      )}
    </div>
  );
}

export default StudyRemindersSection;
